import React, { Fragment, useEffect, useState } from "react";
import { RichText, Text } from "@sitecore-jss/sitecore-jss-react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import Collapsible from "../../Collapsible";
import PDPDeliveryInfo from "../PDPDeliveryInfo";
import { log } from "@assets/js/utils";

import { ArrowSmallSvg } from "@assets/js/icons";
import getUniqueId from "@assets/js/get-unique-id";
import parseSpecifications from "@assets/js/parse-specifications";

import useGlobalStore, { useCommerceMarketSupportsEcom } from "@store/global";

import {
  useActiveProductData,
  useActiveModelData,
  useProductSpecificationsOpen,
} from "@store/pdp";

import "./pdp-product-specification.scss";

const PDPProductSpecification = ({
  items,
  button,
  bottomInfo,
  deliveryTexts,
  defaultDimensionUnit = "Metric",
  careAndMaintenance = "<a></a>",
}) => {
  const { t } = useTranslation();

  const texts = {
    about: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_About"),
    specifications: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Specifications"),
    productDetails: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_ProductDetails"
    ),
    assembly: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Assembly"),
    downloadHere: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_DownloadHere"
    ),
    fabricComposition: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_FabricComposition"
    ),
    woodType: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_WoodType"),
    chairsStackable: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_ChairsStackable"
    ),
    yes: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Yes"),
    no: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_No"),
    chairsStackedCount: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_ChairsStackedCount"
    ),
    productId: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_ProductId"),

    dimensions: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Dimensions"),
    dimensionsMetric: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_DimensionsMetric"),
    dimensionsImperial: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_DimensionsImperial"),
    width: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Width"),
    height: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Height"),
    depth: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Depth"),
    weight: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Weight"),
    cm: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Cm"),
    kg: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Kg"),
    inch: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Inch"),
    lbs: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Lbs"),

    downloads: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Downloads"),

    careAndMaintenance: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_CareAndMaintenance"
    ),
    deliveryAndReturn: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_DeliveryAndReturn"
    ),

    priceDisclaimer: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_PriceDisclaimer"
    ),
    designerHeader: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_DesignerHeader"
    ),
    designerLink: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_DesignerLink"
    ),
    designerName: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_DesignerName"
    ),
    designYear: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_DesignYear"
    ),
    model: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_Model"
    ),
    aboutTheDesigner: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_AboutTheDesigner"
    ),
    aboutTheProduct: t(
        "FritzHansen_Project_ConsumersAndProfessionals_PDP_AboutTheProduct"
    ),
  };
  const sc_lang = useGlobalStore((state) => state.sc_lang);

  const DIMENSION_UNIT = {
    Metric: "Metric",
    Imperial: "Imperial"
  }

  const [commerceMarketSupportsEcom] = useCommerceMarketSupportsEcom();
  const [activeProductData] = useActiveProductData();
  const [activeDimensionFilter, setActiveDimensionFilter] = useState(defaultDimensionUnit);

  const [
    activeModelData,
  ] = useActiveModelData();

  const [
    productSpecificationsOpen,
  ] = useProductSpecificationsOpen();

  const [specAccordions, setSpecAccordions] = useState([]);
  const [dimensionSpecs, setDimensionSpecs] = useState([]);

  useEffect(() => {
    if (activeModelData?.specifications) {
      let s = parseSpecifications(
        activeModelData.specifications,
        sc_lang,
        true
      );

      let nonDimensionSpecs = s.filter(x => x?.value[0]?.sectionId !== "Dimensions" && x?.value[0]?.sectionId !== "ImperialDimensions");
      setSpecAccordions(nonDimensionSpecs);

      let dimensionSpecs = s.filter(x => x?.value[0]?.sectionId === "Dimensions" || x?.value[0]?.sectionId === "ImperialDimensions");
      setDimensionSpecs(dimensionSpecs);
    }
  }, [activeModelData, sc_lang]);

  return (
    <div className="pdp-product-specification" id="productSpecifications">

      {/* <PDPDeliveryInfo modifier="d-n d-b-vp5" /> */}

      <Collapsible
        preExpanded={productSpecificationsOpen}
        triggerComponent={
          <>
            <h2>{texts.about}</h2>
            <span className="pdp-product-specification__icon">
              <ArrowSmallSvg />
            </span>
          </>
        }
        contentComponent={
          <div className="pdp-product-specification__about">
            <h4>{texts.aboutTheProduct.replace('{productType}', activeModelData.ProductType)}</h4>
            {activeProductData.longDescription && <p className="pdp-product-specification__bodytext" dangerouslySetInnerHTML={{ __html: activeProductData.longDescription }}></p>}
            <h4>{texts.aboutTheDesigner}</h4>
            {activeProductData.designerDescription && <p>{activeProductData.designerDescription}</p>}
          </div>
        }
      />

      {dimensionSpecs && dimensionSpecs.length > 0 &&
        <Collapsible
          key={`dimensionAccordion`}
          preExpanded={false}
          triggerComponent={
            <>
              <h2>{texts.specifications}</h2>
              <span className="pdp-product-specification__icon">
                <ArrowSmallSvg />
              </span>
            </>
          }
          contentComponent={
            <>
              {(activeModelData?.imperialResourceImageUrl || activeModelData?.resourceImageUrl) &&
                activeDimensionFilter === DIMENSION_UNIT.Metric && (
                  <img
                    className="pdp-product-specification__image"
                    src={activeModelData.resourceImageUrl ?? activeModelData.imperialResourceImageUrl}
                    alt="measurement pictogram"
                  />
                )}
              {(activeModelData?.imperialResourceImageUrl || activeModelData?.resourceImageUrl) &&
                activeDimensionFilter === DIMENSION_UNIT.Imperial && (
                  <img
                    className="pdp-product-specification__image"
                    src={activeModelData?.imperialResourceImageUrl ?? activeModelData.resourceImageUrl}
                    alt="measurement pictogram"
                  />
                )}
              <div className="pdp-product-specification__dimension-filter">
                <ul>
                  <li 
                    key={`pdp-product-specification__dimension-filter-item-metric`}
                    className={`pdp-product-specification__dimension-filter-item ${activeDimensionFilter === DIMENSION_UNIT.Metric ? "active" : ""}`}>
                    <button onClick={() => { setActiveDimensionFilter(DIMENSION_UNIT.Metric) }}>
                      <Text field={{ value: texts.dimensionsMetric }} tag="span" />
                    </button>
                  </li>
                  <li
                    key={`pdp-product-specification__dimension-filter-item-imperial`}
                    className={`pdp-product-specification__dimension-filter-item ${activeDimensionFilter === DIMENSION_UNIT.Imperial ? "active" : ""}`}>
                    <button onClick={() => { setActiveDimensionFilter(DIMENSION_UNIT.Imperial) }} >
                      <Text field={{ value: texts.dimensionsImperial }} tag="span" />
                    </button>
                  </li>
                </ul>
              </div>
              <table className="pdp-product-specification__table">
                <thead>
                  <tr>
                    <th className="pdp-product-specification__dimension-header">{activeModelData.ProductType}</th>
                    <td></td>
                  </tr>
                </thead>
                {dimensionSpecs.map((item, index) => {
                  if (item?.value[0]?.sectionId === "Dimensions" && activeDimensionFilter === DIMENSION_UNIT.Imperial) {
                    return <Fragment key={`dimensionSpecTable${index}`}></Fragment>;
                  }
                  if (item?.value[0]?.sectionId === "ImperialDimensions" && activeDimensionFilter === DIMENSION_UNIT.Metric) {
                    return <Fragment key={`dimensionSpecTable${index}`}></Fragment>;
                  }

                  return (
                    <tbody key={`dimensionSpecTable${index}`}>
                      {item.value.map((item2, index2) => {
                        return (
                          <tr key={`specAccordionInner${index2}`}>
                            <th>{item2?.name?.toString() || ""}</th>
                            <td>
                              {item2?.value?.toString() || ""}{" "}
                              {item2?.unit?.toString() || ""}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>)
                })}
              </table>

              {specAccordions &&
                specAccordions.length > 0 &&
                specAccordions.map((item, index) => {
                  return <table className="pdp-product-specification__table" key={`specificationTable${index}`}>
                    <thead>
                      <tr>
                        <th className="pdp-product-specification__dimension-header">{item.key}</th>
                        <td></td>
                      </tr>
                    </thead>
                    <tbody>
                      {item.value.map((item2, index2) => {
                        return (
                          <tr key={`specAccordionInner${index2}`}>
                            <th>{item2?.name?.toString() || ""}</th>
                            <td>
                              {item2?.value?.toString() || ""}{" "}
                              {item2?.unit?.toString() || ""}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                })}
            </>
          }
        />
      }

      {activeModelData && activeModelData.downloads && (
        <Collapsible
          preExpanded={false}
          triggerComponent={
            <>
              <h2>{texts.downloads}</h2>
              <span className="pdp-product-specification__icon">
                <ArrowSmallSvg />
              </span>
            </>
          }
          contentComponent={
            <>
              {activeModelData.downloads &&
                activeModelData.downloads.map((link) => {
                  return (
                    <a
                      key={getUniqueId()}
                      className="pdp-product-specification__link pdp-product-specification__link--not-in-table"
                      href={link.url}
                    >
                      {link.name}
                    </a>
                  );
                })}
            </>
          }
        />
      )}

      {commerceMarketSupportsEcom && activeModelData?.deliveryKey && (
        <Collapsible
          preExpanded={false}
          triggerComponent={
            <>
              <h2>{texts.deliveryAndReturn}</h2>
              <span className="pdp-product-specification__icon">
                <ArrowSmallSvg />
              </span>
            </>
          }
          contentComponent={
            <>
              {deliveryTexts &&
                activeModelData.deliveryKey &&
                deliveryTexts[activeModelData.deliveryKey] &&
                <RichText className="pdp-product-specification__bodytext" field={{ value: deliveryTexts[activeModelData.deliveryKey] }} />}
            </>
          }
        />
      )}

      {careAndMaintenance?.value && (
        <Collapsible
          preExpanded={false}
          triggerComponent={
            <>
              <h2>{texts.careAndMaintenance}</h2>
              <span className="pdp-product-specification__icon">
                <ArrowSmallSvg />
              </span>
            </>
          }
          contentComponent={
            <RichText
              field={careAndMaintenance}
              className="pdp-product-specification__bodytext"
            />
          }
        />
      )}

      {/* <NavButton fields={button} />
      <p className="pdp-product-specification__bodytext pdp-product-specification__bodytext--bottom">
        {bottomInfo}
      </p> */}
    </div>
  );
};
PDPProductSpecification.propTypes = {
  items: PropTypes.array,
  button: PropTypes.object,
  bottomInfo: PropTypes.string,
};

export default PDPProductSpecification;
